// Settings UI for "Prime Agent Direct Connect". Lets the user connect their Prime Agent
// account, then link banks that Plaid can't reach. Focus stores the bank
// credentials encrypted and sends a one-shot request to Prime Agent to log in and
// download transactions on demand — no Prime Agent-side polling.

import React, { useCallback, useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  List,
  ListItem,
  ListItemText,
  MenuItem,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  Delete as DeleteIcon,
  Add as AddIcon,
  Link as LinkIcon,
  LinkOff as LinkOffIcon,
  Sync as SyncIcon,
  AccountBalance as BankIcon,
} from "@mui/icons-material";
import {
  primeAgentService,
  PrimeAgentBankLink,
  PrimeAgentConnection,
  PrimeAgentProvider,
} from "../../services/external/primeAgentService";

const PRIME_AGENT_SALES_URL = "/prime-agent";

const PrimeAgentSettings: React.FC = () => {
  const [connection, setConnection] = useState<PrimeAgentConnection>({
    connected: false,
  });
  const [links, setLinks] = useState<PrimeAgentBankLink[]>([]);
  const [providers, setProviders] = useState<PrimeAgentProvider[]>([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);

  // Add-bank dialog
  const [addOpen, setAddOpen] = useState(false);
  const [form, setForm] = useState({
    institutionId: "",
    username: "",
    password: "",
  });

  // OTP relay dialog
  const [otpFor, setOtpFor] = useState<PrimeAgentBankLink | null>(null);
  const [otpCode, setOtpCode] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const conn = await primeAgentService.getConnection();
      setConnection(conn);
      if (conn.connected) {
        const [lk, pv] = await Promise.all([
          primeAgentService.listBankLinks(),
          primeAgentService.listProviders(),
        ]);
        setLinks(lk);
        setProviders(pv);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load Prime Agent");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const handleConnect = async () => {
    setBusy(true);
    setError(null);
    try {
      const conn = await primeAgentService.connect();
      setConnection(conn);
      setInfo("Prime Agent connected.");
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to connect Prime Agent");
    } finally {
      setBusy(false);
    }
  };

  const handleDisconnect = async () => {
    if (!window.confirm("Disconnect Prime Agent? Linked banks will stop syncing."))
      return;
    setBusy(true);
    try {
      await primeAgentService.disconnect();
      setConnection({ connected: false, status: "disconnected" });
      setLinks([]);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to disconnect");
    } finally {
      setBusy(false);
    }
  };

  const handleAddBank = async () => {
    const provider = providers.find((p) => p.id === form.institutionId);
    if (!provider) {
      setError("Pick a bank first.");
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await primeAgentService.addBankLink({
        institutionId: provider.id,
        institutionName: provider.name,
        username: form.username,
        password: form.password,
      });
      setAddOpen(false);
      setForm({ institutionId: "", username: "", password: "" });
      setInfo(`${provider.name} linked. Use Sync to import transactions.`);
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to link bank");
    } finally {
      setBusy(false);
    }
  };

  const handleSync = async (link: PrimeAgentBankLink) => {
    setBusy(true);
    setError(null);
    setInfo(null);
    try {
      const result = await primeAgentService.syncBankLink(link.id);
      if (result.status === "needs_otp") {
        setOtpFor(link);
        setOtpCode("");
      } else if (result.status === "needs_human") {
        setError(
          `${link.institution_name} needs manual attention: ${
            result.reason || "the bank blocked automated login."
          }`
        );
      } else if (result.status === "ok") {
        setInfo(
          `${link.institution_name}: imported ${result.imported ?? 0} transactions.`
        );
      } else {
        setError(result.reason || "Sync failed.");
      }
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Sync failed");
    } finally {
      setBusy(false);
    }
  };

  const handleSubmitOtp = async () => {
    if (!otpFor) return;
    setBusy(true);
    setError(null);
    try {
      const result = await primeAgentService.submitOtp(otpFor.id, otpCode.trim());
      if (result.status === "ok") {
        setInfo(
          `${otpFor.institution_name}: imported ${result.imported ?? 0} transactions.`
        );
        setOtpFor(null);
      } else if (result.status === "needs_otp") {
        setError("That code didn't work — try the latest one.");
      } else {
        setError(result.reason || "Verification failed.");
        setOtpFor(null);
      }
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Verification failed");
    } finally {
      setBusy(false);
    }
  };

  const handleDeleteLink = async (id: string) => {
    if (!window.confirm("Remove this bank link?")) return;
    try {
      await primeAgentService.deleteBankLink(id);
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to remove link");
    }
  };

  if (loading) {
    return <CircularProgress size={20} />;
  }

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
      <Alert severity="info" icon={<BankIcon />}>
        Prime Agent logs into banks that Plaid can&apos;t reach (&quot;Direct
        Connect&quot;) and imports your transactions for you. Your bank
        credentials are encrypted and only sent to Prime Agent for the moment it
        signs in.{" "}
        <a href={PRIME_AGENT_SALES_URL} target="_blank" rel="noreferrer">
          Learn about Prime Agent
        </a>
        .
      </Alert>

      {error && (
        <Alert severity="error" onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {info && (
        <Alert severity="success" onClose={() => setInfo(null)}>
          {info}
        </Alert>
      )}

      {/* Connection state */}
      {!connection.connected ? (
        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          <Button
            variant="contained"
            startIcon={busy ? <CircularProgress size={16} /> : <LinkIcon />}
            disabled={busy}
            onClick={handleConnect}
          >
            Connect Prime Agent
          </Button>
          <Button href={PRIME_AGENT_SALES_URL} target="_blank" rel="noreferrer">
            Don&apos;t have Prime Agent?
          </Button>
        </Box>
      ) : (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <Chip
            color="success"
            icon={<LinkIcon />}
            label={
              connection.verified_at
                ? `Connected · verified ${new Date(
                    connection.verified_at
                  ).toLocaleDateString()}`
                : "Connected"
            }
          />
          <Button
            color="error"
            startIcon={<LinkOffIcon />}
            disabled={busy}
            onClick={handleDisconnect}
          >
            Disconnect
          </Button>
        </Box>
      )}

      {connection.connected && (
        <>
          <Divider />
          <Box>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                mb: 1,
              }}
            >
              <Typography variant="subtitle2">Linked banks</Typography>
              <Button
                size="small"
                startIcon={<AddIcon />}
                onClick={() => setAddOpen(true)}
              >
                Add bank
              </Button>
            </Box>

            {links.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                No banks linked yet. Add one to auto-import transactions.
              </Typography>
            ) : (
              <List dense>
                {links.map((link) => (
                  <ListItem
                    key={link.id}
                    secondaryAction={
                      <Box sx={{ display: "flex", gap: 0.5 }}>
                        <Tooltip title="Sync now">
                          <span>
                            <IconButton
                              edge="end"
                              disabled={busy}
                              onClick={() => handleSync(link)}
                            >
                              <SyncIcon fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>
                        <Tooltip title="Remove">
                          <IconButton
                            edge="end"
                            color="error"
                            onClick={() => handleDeleteLink(link.id)}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    }
                  >
                    <ListItemText
                      primary={link.institution_name}
                      secondary={
                        link.last_status === "error"
                          ? `Error: ${link.last_error || "sync failed"}`
                          : link.last_sync_at
                            ? `Last synced ${new Date(
                                link.last_sync_at
                              ).toLocaleString()}`
                            : "Never synced"
                      }
                    />
                  </ListItem>
                ))}
              </List>
            )}
          </Box>
        </>
      )}

      {/* Add-bank dialog */}
      <Dialog open={addOpen} onClose={() => setAddOpen(false)} fullWidth>
        <DialogTitle>Link a Direct-Connect bank</DialogTitle>
        <DialogContent>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 1 }}>
            <Alert severity="warning">
              Your credentials are encrypted at rest and only decrypted to send
              to Prime Agent when it signs in. They are never shown again.
            </Alert>
            <TextField
              select
              label="Bank"
              value={form.institutionId}
              onChange={(e) =>
                setForm((f) => ({ ...f, institutionId: e.target.value }))
              }
            >
              {providers.length === 0 && (
                <MenuItem disabled value="">
                  No supported banks yet
                </MenuItem>
              )}
              {providers.map((p) => (
                <MenuItem key={p.id} value={p.id}>
                  {p.name}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              label="Online banking username"
              value={form.username}
              onChange={(e) =>
                setForm((f) => ({ ...f, username: e.target.value }))
              }
              autoComplete="off"
            />
            <TextField
              label="Password"
              type="password"
              value={form.password}
              onChange={(e) =>
                setForm((f) => ({ ...f, password: e.target.value }))
              }
              autoComplete="new-password"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            disabled={busy || !form.institutionId || !form.username}
            onClick={handleAddBank}
          >
            Link bank
          </Button>
        </DialogActions>
      </Dialog>

      {/* OTP relay dialog */}
      <Dialog open={!!otpFor} onClose={() => setOtpFor(null)} fullWidth>
        <DialogTitle>Enter your verification code</DialogTitle>
        <DialogContent>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 1 }}>
            <Typography variant="body2" color="text.secondary">
              {otpFor?.institution_name} sent a one-time code to your phone or
              email. Enter it here and we&apos;ll relay it to Prime Agent to finish
              signing in.
            </Typography>
            <TextField
              label="Verification code"
              value={otpCode}
              onChange={(e) => setOtpCode(e.target.value)}
              autoFocus
              inputProps={{ inputMode: "numeric" }}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOtpFor(null)}>Cancel</Button>
          <Button
            variant="contained"
            disabled={busy || otpCode.trim().length < 4}
            onClick={handleSubmitOtp}
          >
            Submit code
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PrimeAgentSettings;
